import React, { useCallback, useEffect, useState } from "react";

// Held-for-review queue. Surfaces every post the generation pipeline
// flagged instead of publishing (truncation at 8k + 16k, malformed JSON,
// AI review exhausted) — batch 2a of the council roadmap. Two action
// paths per the council verdict:
//   - Regenerate with corrected inputs (structural failures — keyword
//     too broad, prompt bloat, etc.)
//   - Edit inline (content-quality tweaks that a full regenerate would
//     overreach on)
//
// Props:
//   api            — backend base URL (usually from env)
//   authFetch      — parent's auth-injecting fetch wrapper
//   onCountChange  — optional. Called with the current held-post count
//                    after each load, so App.js's sidebar can show a
//                    badge on the "Review Queue" nav item.
export default function HeldForReviewQueue({ api, authFetch, onCountChange }) {
  const [heldPosts, setHeldPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editDrafts, setEditDrafts] = useState({}); // { postId: { title, content, meta_description } }
  const [toast, setToast] = useState(null); // { kind: "ok"|"err", text }
  const [auditing, setAuditing] = useState(false);
  // Filter chips above the card list. "All" shows every reason; the
  // other 5 buckets group the 14 known reason values (see
  // failureSummary() for the full mapping).
  const [categoryFilter, setCategoryFilter] = useState("All");
  // Optional per-client filter. Empty string = all clients.
  const [clientFilter, setClientFilter] = useState("");
  const [clientOptions, setClientOptions] = useState([]);

  const loadHeld = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Kick off both fetches in parallel — the widget shouldn't block
      // the queue list on a slower stats query, and vice versa.
      const [postsRes, statsRes] = await Promise.all([
        authFetch(`${api}/api/posts/held?limit=50${clientFilter ? `&client_id=${encodeURIComponent(clientFilter)}` : ""}`),
        authFetch(`${api}/api/posts/stats`).catch(() => null),
      ]);
      if (!postsRes.ok) {
        const body = await postsRes.text();
        throw new Error(body || `HTTP ${postsRes.status}`);
      }
      const data = await postsRes.json();
      const posts = Array.isArray(data?.posts) ? data.posts : [];
      setHeldPosts(posts);
      setClientOptions(Array.isArray(data?.held_clients) ? data.held_clients : []);
      // Tell App.js the current count so the sidebar nav badge can
      // update in real time as posts get regenerated / promoted /
      // newly held during this session.
      if (typeof onCountChange === "function") onCountChange(posts.length);

      if (statsRes && statsRes.ok) {
        try { setStats(await statsRes.json()); } catch { setStats(null); }
      } else {
        setStats(null);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setHeldPosts([]);
      if (typeof onCountChange === "function") onCountChange(0);
    } finally {
      setLoading(false);
    }
  }, [api, authFetch, onCountChange, clientFilter]);

  useEffect(() => { loadHeld(); }, [loadHeld]);

  const flashToast = (kind, text, ms = 4000) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), ms);
  };

  const runAudit = async () => {
    if (auditing) return;
    const ok = window.confirm(
      "Run retroactive QA on the 25 most-recently-published posts that haven't been QA'd yet?\n\n" +
      "This takes ~5-10 min and will flip any failing posts into this queue."
    );
    if (!ok) return;
    setAuditing(true);
    flashToast("ok", "Audit running in the background — takes a few minutes. You can keep using the dashboard.", 10000);
    try {
      const res = await authFetch(`${api}/api/posts/audit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ limit: 25 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        flashToast("err", `Audit failed: ${data?.error || `HTTP ${res.status}`}`, 8000);
        return;
      }
      const { scanned = 0, passed = 0, held = 0, skipped = 0 } = data;
      flashToast(
        "ok",
        `Audit done — scanned ${scanned}, passed ${passed}, held ${held}, skipped ${skipped}. Queue reloaded below.`,
        10000
      );
      await loadHeld();
    } catch (e) {
      flashToast("err", `Audit threw: ${e?.message || e}`, 8000);
    } finally {
      setAuditing(false);
    }
  };

  const regenerate = async (post, newKeyword) => {
    if (busyId) return;
    const keyword = (newKeyword || post.keyword || "").trim();
    if (!keyword) { flashToast("err", "Keyword is required to regenerate."); return; }
    setBusyId(post.id);
    try {
      const res = await authFetch(`${api}/api/posts/${post.id}/regenerate`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ keyword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 202 || data?.held) {
        // Re-generation also truncated. The backend wrote a fresh
        // held_for_review row — reload the queue so the operator sees
        // the new entry instead of wondering what happened.
        flashToast("err", `Regeneration also truncated — held again. (${data?.reason || "unknown"})`);
        await loadHeld();
        return;
      }
      if (!res.ok) {
        flashToast("err", `Regenerate failed: ${data?.error || `HTTP ${res.status}`}`);
        return;
      }
      flashToast("ok", `Regenerated and sent to the Approval Queue: "${data?.post?.title || keyword}".`);
      await loadHeld();
    } catch (e) {
      flashToast("err", `Regenerate threw: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (post) => {
    setEditDrafts((d) => ({
      ...d,
      [post.id]: {
        title: post.title || "",
        content: post.content || "",
        meta_description: post.meta_description || "",
      },
    }));
    setExpandedId(post.id);
  };

  const cancelEdit = (postId) => {
    setEditDrafts((d) => { const n = { ...d }; delete n[postId]; return n; });
  };

  const saveEditAsDraft = async (post) => {
    if (busyId) return;
    const draft = editDrafts[post.id];
    if (!draft) return;
    setBusyId(post.id);
    try {
      const update = await authFetch(`${api}/api/posts/${post.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title: draft.title,
          content: draft.content,
          meta_description: draft.meta_description,
        }),
      });
      if (!update.ok) {
        const body = await update.json().catch(() => ({}));
        flashToast("err", `Save failed: ${body?.error || `HTTP ${update.status}`}`);
        return;
      }
      // Promote the post out of held_for_review into pending_approval so it
      // enters the Approval Queue for a human sign-off. Going to "draft" would
      // mark it approved + publishable with no review, and "published" would
      // skip QA entirely — pending_approval is the safe promotion target now
      // that nothing publishes until approved.
      const promote = await authFetch(`${api}/api/posts/${post.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: "pending_approval" }),
      });
      if (!promote.ok) {
        flashToast("err", "Saved but failed to promote to pending approval — still in queue.");
        await loadHeld();
        return;
      }
      flashToast("ok", "Edited + sent to the Approval Queue for sign-off.");
      cancelEdit(post.id);
      await loadHeld();
    } catch (e) {
      flashToast("err", `Save threw: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  // Human-facing failure summary. Falls back to a guess from the title
  // prefix when generation_metrics isn't available (older held posts).
  //
  // Returns { tag, detail, category, suggestedAction } where:
  //   - tag: short uppercase label for the red pill on the card
  //   - detail: 1-line "why" string
  //   - category: bucket for the filter chip bar
  //         ("Quality" | "Topic" | "Research" | "Structural" | "System" | "Legacy")
  //   - suggestedAction: which action(s) make sense for this reason
  //         ("regenerate" | "edit" | "both")
  //
  // Legacy reasons (pipeline_step1_truncation, truncation_retry_exhausted,
  // json_parse_failed, ai_review_exhausted) preserved exactly as before —
  // they're tagged "Legacy" in category and "both" in action.
  //
  // Phase 1A→4 reasons live in either:
  //   - failure.detail.reason — for routes/content.js, publish-post.js,
  //     citation-tracker, year-validator, artifact-detection emit sites
  //   - failure.event_type — for scheduler.js's "stale_draft_blocked" and
  //     dedup paths' "duplicate_topic_blocked" (these write held_reason
  //     to the posts row but don't put a `reason` field inside detail)
  const failureSummary = (post) => {
    const detailReason = post?.failure?.detail?.reason;
    const eventType = post?.failure?.event_type;
    // Phase 5.4 fallback: when neither failure.detail.reason nor
    // failure.event_type is present (older holds, or the scheduler /
    // self-repair paths that only stamped the posts.held_reason column
    // without a failure_events row), fall back to the held_reason column.
    // This lets operators triage the held backlog by specific reason
    // instead of a uniform "HELD" pill. A value that matches a known
    // reason here routes into the same specific handlers below
    // (e.g. qa_exhausted_after_repair, taxonomy_incomplete,
    // stale_pipeline_version, duplicate_topic_blocked).
    const heldReason = post?.held_reason;
    const reason = detailReason || eventType || heldReason;

    // ── Legacy reasons (preserved exactly) ─────────────────────────
    if (reason === "pipeline_step1_truncation" || reason === "truncation_retry_exhausted") {
      const attempts = post?.failure?.detail?.attempts || [];
      const budgets = attempts.map((a) => `${a.budget || a.max_tokens}t`).join(" → ");
      return {
        tag: "TRUNCATED",
        detail: `Draft hit the token ceiling. Budgets tried: ${budgets || "—"}`,
        category: "Legacy",
        suggestedAction: "both",
      };
    }
    // Phase 7 qa-followup — STEP9_READABILITY rewrite truncated the
    // body tail (typically mid-FAQ). Pre-QA hold, doesn't burn the
    // 4-cycle QA repair loop. Operator sees a specific cause instead
    // of generic qa_exhausted_after_repair.
    if (reason === "faq_truncation") {
      const tailReason = post?.failure?.detail?.tail_reason || "unknown";
      const tailSample = post?.failure?.detail?.tail_sample;
      return {
        tag: "FAQ TRUNCATED",
        detail: `Step 9 rewrite truncated the body tail (${tailReason}).${tailSample ? ` Last visible: "…${tailSample}"` : ""} Regenerate to retry with a longer ceiling.`,
        category: "Pipeline",
        suggestedAction: "regenerate",
      };
    }
    if (reason === "json_parse_failed") {
      return {
        tag: "MALFORMED_JSON",
        detail: `Model returned end_turn but the JSON didn't parse: ${post?.failure?.detail?.error || "—"}`,
        category: "Legacy",
        suggestedAction: "both",
      };
    }
    if (reason === "ai_review_exhausted") {
      return {
        tag: "QA UNAVAILABLE",
        detail: "AI content review failed after 3 retries — needs human eyes before publish.",
        category: "Legacy",
        suggestedAction: "both",
      };
    }

    // ── Phase 1A: artifact + year + metadata + QA repair ───────────
    if (reason === "artifact_detected" || reason === "artifact_detected_pre_save" || reason === "artifact_detected_pre_rewrite") {
      const issues = post?.failure?.detail?.issues || [];
      const sample = issues.slice(0, 2).map((i) => i.match || i.type || i).join(", ");
      return {
        tag: "AI ARTIFACT",
        detail: sample
          ? `Detected AI artifact patterns: ${sample}${issues.length > 2 ? ` (+${issues.length - 2} more)` : ""}.`
          : "Detected AI artifact patterns (em-dashes, hedging, broken sentences).",
        category: "Quality",
        suggestedAction: "both",
      };
    }
    if (reason === "unsupported_year") {
      const years = post?.failure?.detail?.years || [];
      return {
        tag: "STALE YEAR",
        detail: years.length
          ? `Draft references unsupported year(s): ${years.join(", ")}.`
          : "Draft references a stale or unsupported year (not in the industry whitelist).",
        category: "Quality",
        suggestedAction: "both",
      };
    }
    if (reason === "missing_metadata") {
      const missing = post?.failure?.detail?.missing_metadata || [];
      return {
        tag: "MISSING META",
        detail: missing.length
          ? `Draft is missing required metadata: ${missing.join(", ")}.`
          : "Draft is missing required metadata (title, slug, meta description, or FAQs).",
        category: "Quality",
        suggestedAction: "both",
      };
    }
    if (reason === "qa_exhausted_after_repair") {
      return {
        tag: "QA EXHAUSTED",
        detail: "QA repair loop ran but couldn't get the post to pass — needs human review before publish.",
        category: "Quality",
        suggestedAction: "edit",
      };
    }

    // ── Phase 3: citation validation ───────────────────────────────
    if (reason === "citation_validation_failed") {
      const failed = post?.failure?.detail?.failed_facts || post?.failure?.detail?.errors || [];
      return {
        tag: "CITATION FAIL",
        detail: failed.length
          ? `Citation tracker rejected ${failed.length} fact(s) — likely stale, unverified, or paraphrase too long.`
          : "Citation tracker rejected one or more facts — regenerate to re-fetch research.",
        category: "Research",
        suggestedAction: "regenerate",
      };
    }

    // ── Phase 4: outline validation ────────────────────────────────
    if (reason === "outline_validation_failed") {
      return {
        tag: "OUTLINE FAIL",
        detail: post?.failure?.detail?.error || "Step 1A outline didn't pass the contract (need 3-8 sections with H2 + theme + intendedSection).",
        category: "Structural",
        suggestedAction: "regenerate",
      };
    }

    // ── Phase 1A: stale pipeline version (scheduler) ───────────────
    // Event_type form because scheduler.js emits "stale_draft_blocked"
    // without a detail.reason field. The held_reason on the posts row
    // is "stale_pipeline_version".
    if (reason === "stale_draft_blocked" || reason === "stale_pipeline_version") {
      const draftV = post?.failure?.detail?.draft_pipeline_version || "?";
      const currentV = post?.failure?.detail?.current_pipeline_version || "?";
      return {
        tag: "STALE DRAFT",
        detail: `Draft was generated by an old pipeline (v${draftV}) and the current pipeline is v${currentV}. Regenerate to use the current contract.`,
        category: "System",
        suggestedAction: "regenerate",
      };
    }

    // ── Topic dedup (pre-generation gate) ──────────────────────────
    // Same dual-form: detail.reason OR event_type
    if (reason === "duplicate_topic" || reason === "duplicate_topic_blocked") {
      const conflict = post?.failure?.detail?.conflicting_keyword || post?.failure?.detail?.match || "";
      return {
        tag: "DUPLICATE TOPIC",
        detail: conflict
          ? `Keyword conflicts with an already-published topic: "${conflict}". Regenerate with a more specific or different angle.`
          : "Keyword conflicts with an already-published topic. Regenerate with a more specific keyword.",
        category: "Topic",
        suggestedAction: "regenerate",
      };
    }

    // ── Retroactive audit (posts.js audit endpoint) ────────────────
    if (reason === "retroactive_audit_failed") {
      return {
        tag: "AUDIT FAIL",
        detail: "Retroactive QA scan found issues in a previously-published post. Edit to fix, or regenerate.",
        category: "Quality",
        suggestedAction: "both",
      };
    }

    // ── Phase 6A.1 scheduler-block reasons ─────────────────────────
    // The scheduler refuses to pick a candidate when one of these
    // gates fails and writes a placeholder held row so the operator
    // sees the block in the queue. Each maps to a distinct fix path:
    //   - client_ratio_unconfigured → open client settings, set ratio
    //   - taxonomy_incomplete → open keyword library, tag the keyword
    //   - missing_sap → create the service area page, then add it to
    //                   the client's service_area_pages list
    //   - cannibalization_risk → operator decides: skip, refresh the
    //                            prior post, or edit the keyword/intent
    //                            to differentiate
    if (reason === "client_ratio_unconfigured") {
      return {
        tag: "RATIO UNSET",
        detail: "Scheduler can't run for this client — local/authority ratio isn't configured. Open client settings to set it.",
        category: "Setup",
        suggestedAction: "edit",
      };
    }
    if (reason === "taxonomy_incomplete") {
      const missing = post?.failure?.detail?.missing_fields || [];
      return {
        tag: "TAXONOMY INCOMPLETE",
        detail: missing.length
          ? `Keyword is missing taxonomy: ${missing.join(", ")}. Tag it in the keyword library to make it scheduleable.`
          : "Keyword is missing one or more taxonomy fields (type / service / location / intent). Tag it in the keyword library to make it scheduleable.",
        category: "Setup",
        suggestedAction: "edit",
      };
    }
    if (reason === "missing_sap") {
      const loc = post?.failure?.detail?.needed_location || post?.taxonomy_location || "—";
      return {
        tag: "MISSING SAP",
        detail: `No service area page configured for "${loc}". Create the SAP and add its URL to the client's service_area_pages list.`,
        category: "Setup",
        suggestedAction: "edit",
      };
    }
    if (reason === "cannibalization_risk") {
      const priorTitle = post?.failure?.detail?.prior_title || "an existing post";
      const ageDays = post?.failure?.detail?.age_days;
      const ageNote = ageDays != null ? ` (published ${ageDays}d ago)` : "";
      return {
        tag: "CANNIBALIZATION",
        detail: `Conflicts with "${priorTitle}"${ageNote} on the same service+location+intent. Refresh the existing post, or differentiate the keyword/intent.`,
        category: "Topic",
        suggestedAction: "edit",
      };
    }
    // ── Phase 6A.5 scheduler-block reasons (service-area filter) ───
    // service_locations_unconfigured: operator hasn't declared what
    //   locations the client services. Scheduler refuses all local
    //   keywords. One sentinel row per client.
    // out_of_service_area: the keyword's taxonomy_location isn't in
    //   the client's configured service_locations list. Per-keyword
    //   row. Operator decides: add location, or skip the keyword.
    if (reason === "service_locations_unconfigured") {
      return {
        tag: "LOCATIONS UNSET",
        detail: "Client has local keywords but no service locations are configured. Open client settings → add Service Locations.",
        category: "Setup",
        suggestedAction: "edit",
      };
    }
    if (reason === "out_of_service_area") {
      const loc = post?.failure?.detail?.needed_location || post?.taxonomy_location || "—";
      return {
        tag: "OUT OF AREA",
        detail: `Keyword targets "${loc}" but this client doesn't service that location. Add the location to client's Service Locations list, or skip/remove this keyword.`,
        category: "Setup",
        suggestedAction: "edit",
      };
    }

    // ── Legacy title-prefix fallback for very old held rows ────────
    if ((post.title || "").startsWith("[TRUNCATED]")) {
      return { tag: "TRUNCATED", detail: "Draft truncated (legacy hold, no detailed metrics).", category: "Legacy", suggestedAction: "both" };
    }
    if ((post.title || "").startsWith("[MALFORMED_JSON]")) {
      return { tag: "MALFORMED_JSON", detail: "JSON parse failure (legacy hold, no detailed metrics).", category: "Legacy", suggestedAction: "both" };
    }

    // Phase 5.4: posts.held_reason column fallback. The reason value
    // carried by this row didn't match any specific handler above, but
    // the posts row still records *why* it was held — surface that value
    // (humanized) so the operator can triage by the actual cause instead
    // of a uniform "HELD" pill. Covers scheduler/self-repair holds and
    // any future held_reason the dashboard doesn't yet have a tailored
    // handler for. Only the ~49 NULL-held_reason rows hit the generic
    // return below.
    if (heldReason) {
      const humanized = String(heldReason).replace(/_/g, " ").trim();
      return {
        tag: (humanized || "HELD").toUpperCase(),
        detail: `Held: ${humanized}. No structured failure detail was recorded for this post — review manually, then regenerate or edit inline.`,
        category: "Legacy",
        suggestedAction: "both",
      };
    }
    return { tag: "HELD", detail: "Post held for manual review.", category: "Legacy", suggestedAction: "both" };
  };

  // Human-readable "What to try:" hint per suggestedAction
  const actionHint = (failure) => {
    if (failure.suggestedAction === "regenerate") return "What to try: regenerate with a corrected keyword or input.";
    if (failure.suggestedAction === "edit") return "What to try: edit inline to fix the specific issue, then promote to draft.";
    return "What to try: regenerate (structural issues) or edit inline (content tweaks).";
  };

  if (loading) {
    return <div style={{ color: "#888", padding: 40, textAlign: "center" }}>Loading review queue…</div>;
  }

  // Precompute failure metadata for every post once — used by both the
  // category filter and the per-card rendering below, so the filter
  // doesn't have to call failureSummary() twice per post.
  const postsWithFailure = heldPosts.map((post) => ({ post, failure: failureSummary(post) }));

  // Available category buckets, in display order. "All" is always
  // first and unfiltered. We compute counts per bucket so the chip
  // labels show e.g. "Quality 4".
  // Phase 6A.1: "Setup" bucket added for scheduler-block reasons
  // (client_ratio_unconfigured, taxonomy_incomplete, missing_sap).
  // Placed right after "All" because these are operator action items
  // that gate the entire pipeline for a client — high priority.
  const categoryOrder = ["All", "Setup", "Quality", "Topic", "Research", "Structural", "System", "Legacy"];
  const categoryCounts = postsWithFailure.reduce((acc, { failure }) => {
    acc.All = (acc.All || 0) + 1;
    acc[failure.category] = (acc[failure.category] || 0) + 1;
    return acc;
  }, {});

  // The client filter is applied server-side so the browser never has to
  // download every full article just to select one client.
  const visiblePosts = postsWithFailure.filter(({ post, failure }) => {
    if (categoryFilter !== "All" && failure.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div>
      {/* Header row with count + refresh */}
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>Queue</div>
          <div style={styles.headerCount}>
            {heldPosts.length} post{heldPosts.length === 1 ? "" : "s"} held for review
          </div>
          <div style={styles.headerSub}>
            Posts the generation pipeline flagged instead of publishing. Regenerate with a corrected input or edit inline and promote back to draft.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={runAudit} style={styles.auditBtn} disabled={auditing} title="Run QA on the 25 most-recently-published posts that haven't been QA'd yet. Catches artifacts from posts published before QA was restored.">
            {auditing ? "Auditing…" : "⚙ Audit old posts"}
          </button>
          <button onClick={loadHeld} style={styles.refreshBtn}>↻ Refresh</button>
        </div>
      </div>

      {/* Stats widget — 3 tiles. Truncation rate is the council's
          signal metric (>15% = promote to staged pipeline). Oldest
          held age surfaces backlog that's rotting in the queue. */}
      {stats && <QueueStats stats={stats} />}

      {error && (
        <div style={styles.errorBanner}>
          <strong>Failed to load queue.</strong> {error}
        </div>
      )}

      {toast && (
        <div style={{ ...styles.toast, background: toast.kind === "ok" ? "rgba(34,197,94,0.15)" : "rgba(214,0,0,0.15)", borderColor: toast.kind === "ok" ? "#22C55E" : "#D60000", color: toast.kind === "ok" ? "#86efac" : "#fca5a5" }}>
          {toast.text}
        </div>
      )}

      {/* Filter chip row + client dropdown. Hidden when the queue is
          empty so we don't show useless filters above an empty state. */}
      {heldPosts.length > 0 && (
        <div style={styles.filterRow}>
          <div style={styles.filterChips}>
            {categoryOrder.map((cat) => {
              const count = categoryCounts[cat] || 0;
              if (cat !== "All" && count === 0) return null; // hide empty buckets except All
              const active = categoryFilter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  style={{
                    ...styles.filterChip,
                    ...(active ? styles.filterChipActive : {}),
                  }}
                >
                  {cat}
                  <span style={{ ...styles.filterChipCount, opacity: active ? 1 : 0.6 }}>{count}</span>
                </button>
              );
            })}
          </div>
          {clientOptions.length > 1 && (
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              style={styles.clientSelect}
            >
              <option value="">All clients</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {heldPosts.length === 0 && !error && (
        <div style={styles.empty}>
          <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}>✓</div>
          <div>Queue is clear. No posts are currently held for review.</div>
        </div>
      )}

      {/* Filtered-but-empty state — when the queue has posts but the
          chosen filter combo hides all of them. Different copy than the
          "queue is clear" state above so Suzanne knows to widen filters. */}
      {heldPosts.length > 0 && visiblePosts.length === 0 && (
        <div style={styles.empty}>
          <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}>⌕</div>
          <div>No posts match the current filter.</div>
          <button
            onClick={() => { setCategoryFilter("All"); setClientFilter(""); }}
            style={{ ...styles.refreshBtn, marginTop: 12 }}
          >Reset filters</button>
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {visiblePosts.map(({ post, failure }) => {
          const isExpanded = expandedId === post.id;
          const isEditing = !!editDrafts[post.id];
          const isBusy = busyId === post.id;
          const clientName = post?.clients?.name || "—";
          const industry = post?.clients?.industry || "";
          // Action gating — hide the action that doesn't make sense
          // for this reason (e.g. duplicate_topic can't be fixed by
          // editing inline; the topic itself is the problem).
          const showRegenerate = failure.suggestedAction !== "edit";
          const showEdit = failure.suggestedAction !== "regenerate";
          return (
            <div key={post.id} style={styles.card}>
              {/* Row 1: reason tag + keyword + client */}
              <div style={styles.cardHead}>
                <span style={styles.reasonTag}>{failure.tag}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.keyword}>{post.keyword || <em style={{ color: "#555" }}>no keyword</em>}</div>
                  <div style={styles.meta}>
                    {clientName}{industry ? ` · ${industry}` : ""} · {new Date(post.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : post.id)}
                  style={styles.expandBtn}
                  disabled={isBusy}
                >{isExpanded ? "Collapse" : "Details"}</button>
              </div>
              <div style={styles.failureDetail}>{failure.detail}</div>
              <div style={styles.actionHint}>{actionHint(failure)}</div>

              {isExpanded && (
                <div style={styles.expanded}>
                  {!isEditing && (
                    <>
                      <div style={styles.sectionLabel}>Current state</div>
                      <div style={styles.preview}>
                        <div><strong style={{ color: "#888" }}>Title:</strong> {post.title || <em style={{ color: "#555" }}>—</em>}</div>
                        <div><strong style={{ color: "#888" }}>Slug:</strong> {post.slug || <em style={{ color: "#555" }}>—</em>}</div>
                        <div><strong style={{ color: "#888" }}>Meta:</strong> {post.meta_description || <em style={{ color: "#555" }}>—</em>}</div>
                        {post.content && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: "pointer", color: "#888" }}>
                              Body ({post.content.length} chars)
                            </summary>
                            <pre style={styles.codeBlock}>{post.content.slice(0, 4000)}{post.content.length > 4000 ? "\n… (truncated in preview — full content available via edit)" : ""}</pre>
                          </details>
                        )}
                      </div>

                      <div style={styles.sectionLabel}>Actions</div>
                      <div style={styles.actionRow}>
                        {showRegenerate && (
                          <RegenerateForm post={post} onSubmit={regenerate} disabled={isBusy} />
                        )}
                        {showEdit && (
                          <button onClick={() => startEdit(post)} style={styles.secondaryBtn} disabled={isBusy}>
                            Edit inline
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {isEditing && (
                    <InlineEditor
                      post={post}
                      draft={editDrafts[post.id]}
                      onChange={(patch) => setEditDrafts((d) => ({ ...d, [post.id]: { ...d[post.id], ...patch } }))}
                      onCancel={() => cancelEdit(post.id)}
                      onSave={() => saveEditAsDraft(post)}
                      disabled={isBusy}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QueueStats({ stats }) {
  const {
    currently_held = 0,
    oldest_held_age_hours,
    truncation_rate_14d = 0,
    truncations_14d = 0,
    recoveries_14d = 0,
    held_14d = 0,
    publishes_14d = 0,
    council_threshold_pct = 15,
  } = stats || {};

  // Tile color logic:
  //   - Held count: neutral if 0, warn at 1+, alarm at 10+
  //   - Oldest age: neutral <24h, warn 24-48h, alarm >48h (council threshold)
  //   - Truncation rate: neutral < threshold, alarm >= threshold
  const heldTone = currently_held === 0 ? "ok" : currently_held >= 10 ? "alarm" : "warn";
  const ageTone = oldest_held_age_hours == null
    ? "ok"
    : oldest_held_age_hours >= 48 ? "alarm"
      : oldest_held_age_hours >= 24 ? "warn" : "ok";
  const truncTone = truncation_rate_14d >= council_threshold_pct ? "alarm"
    : truncation_rate_14d >= council_threshold_pct * 0.66 ? "warn" : "ok";

  const ageDisplay = oldest_held_age_hours == null
    ? "—"
    : oldest_held_age_hours < 1 ? "< 1h"
      : oldest_held_age_hours < 24 ? `${Math.round(oldest_held_age_hours)}h`
        : `${Math.round(oldest_held_age_hours / 24 * 10) / 10}d`;

  return (
    <div style={statStyles.grid}>
      <StatTile
        tone={heldTone}
        label="Held now"
        value={currently_held}
        sub={held_14d === currently_held ? "" : `${held_14d} held total past 14d`}
      />
      <StatTile
        tone={ageTone}
        label="Oldest held"
        value={ageDisplay}
        sub={ageTone === "alarm" ? "> 48h — address or deprioritize" : ageTone === "warn" ? "> 24h in queue" : ""}
      />
      <StatTile
        tone={truncTone}
        label="Truncation rate (14d)"
        value={`${truncation_rate_14d}%`}
        sub={
          truncTone === "alarm"
            ? `Above ${council_threshold_pct}% threshold — promote to staged pipeline`
            : truncTone === "warn"
              ? `Trending toward ${council_threshold_pct}% threshold`
              : `${truncations_14d} hit / ${recoveries_14d} recovered / ${publishes_14d} clean publishes`
        }
      />
    </div>
  );
}

function StatTile({ tone, label, value, sub }) {
  const palette = {
    ok:    { border: "#1a1a1a",              accent: "#22C55E", bg: "rgba(34,197,94,0.05)" },
    warn:  { border: "rgba(234,179,8,0.35)", accent: "#EAB308", bg: "rgba(234,179,8,0.05)" },
    alarm: { border: "rgba(214,0,0,0.45)",   accent: "#D60000", bg: "rgba(214,0,0,0.08)" },
  }[tone] || { border: "#1a1a1a", accent: "#888", bg: "transparent" };

  return (
    <div style={{ ...statStyles.tile, border: `1px solid ${palette.border}`, background: palette.bg }}>
      <div style={{ ...statStyles.tileLabel, color: palette.accent }}>{label}</div>
      <div style={statStyles.tileValue}>{value}</div>
      {sub ? <div style={statStyles.tileSub}>{sub}</div> : <div style={{ height: 15 }} />}
    </div>
  );
}

const statStyles = {
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 18 },
  tile: { padding: 14, borderRadius: 4, background: "#0d0d0d" },
  tileLabel: { fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 },
  tileValue: { fontSize: 28, fontWeight: 600, color: "#fff", margin: "4px 0 6px", fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em", fontVariantNumeric: "tabular-nums" },
  tileSub: { fontSize: 11, color: "#888", lineHeight: 1.4 },
};

function RegenerateForm({ post, onSubmit, disabled }) {
  const [kw, setKw] = useState(post.keyword || "");
  return (
    <div style={{ display: "flex", gap: 8, flex: 1, minWidth: 280 }}>
      <input
        type="text"
        value={kw}
        onChange={(e) => setKw(e.target.value)}
        placeholder="Keyword (edit if the old one was too broad)"
        style={styles.kwInput}
        disabled={disabled}
      />
      <button
        onClick={() => onSubmit(post, kw)}
        style={styles.primaryBtn}
        disabled={disabled || !kw.trim()}
      >{disabled ? "Regenerating…" : "Regenerate"}</button>
    </div>
  );
}

function InlineEditor({ post, draft, onChange, onCancel, onSave, disabled }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label style={styles.fieldLabel}>Title</label>
      <input
        type="text"
        value={draft.title}
        onChange={(e) => onChange({ title: e.target.value })}
        style={styles.textInput}
        disabled={disabled}
      />
      <label style={styles.fieldLabel}>Meta description</label>
      <input
        type="text"
        value={draft.meta_description}
        onChange={(e) => onChange({ meta_description: e.target.value })}
        style={styles.textInput}
        disabled={disabled}
      />
      <label style={styles.fieldLabel}>Content (HTML)</label>
      <textarea
        value={draft.content}
        onChange={(e) => onChange({ content: e.target.value })}
        style={styles.textarea}
        rows={18}
        disabled={disabled}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={styles.secondaryBtn} disabled={disabled}>Cancel</button>
        <button onClick={onSave} style={styles.primaryBtn} disabled={disabled}>
          {disabled ? "Saving…" : "Save + promote to draft"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 },
  eyebrow: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" },
  headerCount: { fontSize: 24, color: "#fff", fontWeight: 600, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.02em" },
  headerSub: { fontSize: 12, color: "#777", maxWidth: 640, marginTop: 4, lineHeight: 1.5 },
  refreshBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#999", padding: "6px 14px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif" },
  auditBtn:   { background: "rgba(214,0,0,0.12)", border: "1px solid rgba(214,0,0,0.35)", color: "#fca5a5", padding: "6px 14px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", whiteSpace: "nowrap" },
  empty: { padding: "60px 20px", textAlign: "center", color: "#666", border: "1px dashed #1a1a1a", borderRadius: 4 },
  errorBanner: { padding: 12, border: "1px solid rgba(214,0,0,0.4)", background: "rgba(214,0,0,0.08)", color: "#fca5a5", borderRadius: 4, marginBottom: 12, fontSize: 13 },
  toast: { padding: "10px 14px", borderRadius: 4, border: "1px solid", marginBottom: 12, fontSize: 13 },
  card: { background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 4, padding: 14 },
  cardHead: { display: "flex", alignItems: "center", gap: 12 },
  reasonTag: { background: "rgba(214,0,0,0.15)", border: "1px solid rgba(214,0,0,0.4)", color: "#fca5a5", padding: "3px 10px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", borderRadius: 3, whiteSpace: "nowrap" },
  keyword: { color: "#fff", fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  meta: { fontSize: 11, color: "#666", marginTop: 2 },
  expandBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#888", padding: "4px 12px", fontSize: 11, cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase" },
  failureDetail: { marginTop: 8, fontSize: 12, color: "#888", paddingLeft: 2 },
  actionHint: { marginTop: 4, fontSize: 11, color: "#555", paddingLeft: 2, fontStyle: "italic" },
  filterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 },
  filterChips: { display: "flex", flexWrap: "wrap", gap: 6 },
  filterChip: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#888",
    padding: "5px 12px",
    fontSize: 11,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    cursor: "pointer",
    borderRadius: 999,
    fontFamily: "'Barlow Condensed', sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  filterChipActive: {
    background: "rgba(214,0,0,0.15)",
    border: "1px solid rgba(214,0,0,0.4)",
    color: "#fca5a5",
  },
  filterChipCount: {
    fontSize: 10,
    background: "rgba(255,255,255,0.06)",
    padding: "1px 6px",
    borderRadius: 999,
    fontVariantNumeric: "tabular-nums",
  },
  clientSelect: {
    background: "#070707",
    border: "1px solid #2a2a2a",
    color: "#aaa",
    padding: "5px 10px",
    fontSize: 11,
    borderRadius: 3,
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.06em",
    cursor: "pointer",
    minWidth: 180,
  },
  expanded: { marginTop: 14, paddingTop: 14, borderTop: "1px solid #1a1a1a" },
  sectionLabel: { fontSize: 10, color: "#555", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 6, marginTop: 8 },
  preview: { background: "#070707", border: "1px solid #1a1a1a", borderRadius: 3, padding: 10, fontSize: 12, color: "#aaa", display: "grid", gap: 4 },
  codeBlock: { background: "#050505", padding: 10, borderRadius: 3, fontSize: 11, color: "#888", whiteSpace: "pre-wrap", overflow: "auto", maxHeight: 320, marginTop: 6 },
  actionRow: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  kwInput: { flex: 1, background: "#070707", border: "1px solid #2a2a2a", color: "#fff", padding: "7px 10px", fontSize: 13, borderRadius: 3, minWidth: 0 },
  primaryBtn: { background: "#d60000", border: "none", color: "#fff", padding: "7px 16px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", whiteSpace: "nowrap" },
  secondaryBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#aaa", padding: "7px 16px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 3, fontFamily: "'Barlow Condensed', sans-serif", whiteSpace: "nowrap" },
  fieldLabel: { fontSize: 10, color: "#666", letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" },
  textInput: { background: "#070707", border: "1px solid #2a2a2a", color: "#fff", padding: "8px 10px", fontSize: 13, borderRadius: 3, width: "100%", boxSizing: "border-box" },
  textarea: { background: "#070707", border: "1px solid #2a2a2a", color: "#ddd", padding: 10, fontSize: 12, borderRadius: 3, width: "100%", fontFamily: "ui-monospace, 'Cascadia Code', Menlo, monospace", boxSizing: "border-box", resize: "vertical" },
};
